import { useFeatureGate } from '@statsig/react-bindings';
import { useAtomValue } from 'jotai';
import React, { PropsWithChildren } from 'react';

import { useTranslate } from '@growtherapy/i18n';
import { Card, Display, Text, cn, useRebrand } from '@growtherapy/sprout-ui';

import PreSessionImage from '../../../assets/images/pre-session-image.svg?react';
import { useAdmissionFlow } from '../../../hooks/useAdmissionFlow';
import { telehealthAutoOpenIncompleteMeasuresDrawer } from '../../../statsig/gates';
import { videoRoomTokenAtom } from '../../../video-sdk/core/room/state';
import { VisitorDrawerState, doesClientRequireSafetyGuardsAtom, visitorMeasuresAtom } from '../../state';
import { WaitingRoomState } from '../types';
import { ClientMeasuresCta } from './ClientMeasuresCta';
import { JoinSessionCta } from './JoinSessionCta';
import { MoodSurveyCta } from './MoodSurveyCta';
import { ReflectionTopicsCtaWrapper } from './ReflectionTopicsCta';
import { VibesCtaWrapper } from './VibesCta';

export interface PreSessionPromptProps {
  callToActionContent: React.ReactNode;
  hasOutstandingMeasures: boolean;
  isAutoOpenMeasuresDrawerEnabled: boolean;
  state?: WaitingRoomState;
}

export type CTAProps = {
  openDrawer: (drawer: VisitorDrawerState) => void;
  state?: WaitingRoomState;
  variant?: PreSessionPromptVariant;
};

export enum PreSessionPromptVariant {
  CLIENT_MEASURES = 'CLIENT_MEASURES',
  MOOD_SURVEY = 'MOOD_SURVEY',
  REFLECTION_TOPICS = 'REFLECTION_TOPICS',
  VIBES = 'VIBES',
}

export const VariantComponents: Record<PreSessionPromptVariant, React.FC<CTAProps>> = {
  [PreSessionPromptVariant.CLIENT_MEASURES]: ClientMeasuresCta,
  [PreSessionPromptVariant.MOOD_SURVEY]: MoodSurveyCta,
  [PreSessionPromptVariant.VIBES]: VibesCtaWrapper,
  [PreSessionPromptVariant.REFLECTION_TOPICS]: ReflectionTopicsCtaWrapper,
};

function BodyContent({
  isAutoOpenMeasuresDrawerEnabled,
  hasOutstandingMeasures,
  state,
}: {
  isAutoOpenMeasuresDrawerEnabled: boolean;
  hasOutstandingMeasures: boolean;
  state?: WaitingRoomState;
}) {
  const t = useTranslate();
  if (state === WaitingRoomState.ADMITTED) {
    if (hasOutstandingMeasures && isAutoOpenMeasuresDrawerEnabled) {
      return (
        <span>
          {t('telehealth-core:waitingRoom.yourProviderIsReadyForYou')}{' '}
          {t('telehealth-core:waitingRoom.youCanFinishYourHealthCheckInFormsNowOrAfterTheSessionStarts')}
        </span>
      );
    }
    return t('telehealth-core:waitingRoom.yourProviderIsReadyForYou');
  }
  if (isAutoOpenMeasuresDrawerEnabled) {
    if (hasOutstandingMeasures) {
      return (
        <span>
          {t('telehealth-core:waitingRoom.yourProviderWillStartYourSessionSoon')}{' '}
          {t('telehealth-core:waitingRoom.youCanFinishYourHealthCheckInFormsNowOrAfterTheSessionStarts')}
        </span>
      );
    }
    return t('telehealth-core:waitingRoom.yourProviderWillStartYourSessionSoon');
  }
  return (
    <span>
      {t('telehealth-core:waitingRoom.youCanJoinWhenYourProviderIsReady')}
      <br />
      {t('telehealth-core:waitingRoom.theyHave10MinGracePeriod')}
    </span>
  );
}

export function PreSessionPrompt({
  callToActionContent,
  hasOutstandingMeasures,
  isAutoOpenMeasuresDrawerEnabled,
  state,
}: PreSessionPromptProps) {
  const t = useTranslate();
  const isRebrand = useRebrand();
  const { headingContent, bodyContent } = {
    headingContent:
      state === WaitingRoomState.ADMITTED
        ? t('telehealth-core:waitingRoom.timeToJoinYourSession')
        : t('telehealth-core:waitingRoom.youreInTheRightPlace'),
    bodyContent: (
      <BodyContent
        isAutoOpenMeasuresDrawerEnabled={isAutoOpenMeasuresDrawerEnabled}
        hasOutstandingMeasures={hasOutstandingMeasures}
        state={state}
      />
    ),
  };

  return (
    <Card className='flex flex-col mobile:border-x rounded-xl items-center max-w-[38rem] gap-y-10 sm:p-10'>
      {!isRebrand && (
        <div className='hidden sm:block'>
          <PreSessionImage
            aria-label={t('telehealth-core:waitingRoom.aLightPurpleSketchOfAPairOfHandsClaspedTogether')}
          />
        </div>
      )}

      <div className='flex flex-col gap-y-4 items-center'>
        {isRebrand ? (
          <Display.Small>{headingContent}</Display.Small>
        ) : (
          <Text as='h2' variant='lg' className='font-medium'>
            {headingContent}
          </Text>
        )}

        <Text className={cn('text-neutral-700 text-center', { 'max-w-[30rem]': isAutoOpenMeasuresDrawerEnabled })}>
          {bodyContent}
        </Text>

        {callToActionContent}
      </div>
    </Card>
  );
}

export function PreSessionPromptWrapper({
  isLoading,
  variant,
  openDrawer,
}: {
  isLoading: boolean;
  variant?: PreSessionPromptVariant;
  openDrawer: (drawer: VisitorDrawerState) => void;
}) {
  const admissionFlow = useAdmissionFlow();
  const hasRoomToken = !!useAtomValue(videoRoomTokenAtom);
  const isAdmitted = admissionFlow === 'manual' && hasRoomToken;
  const { value: isAutoOpenMeasuresDrawerEnabled } = useFeatureGate(telehealthAutoOpenIncompleteMeasuresDrawer);
  const visitorMeasures = useAtomValue(visitorMeasuresAtom);
  const doesClientRequireSafetyGuards = useAtomValue(doesClientRequireSafetyGuardsAtom);
  const hasOutstandingMeasures = !!visitorMeasures?.length && !doesClientRequireSafetyGuards;

  let CallToAction: React.FC<PropsWithChildren<CTAProps>> = React.Fragment;
  if (isAdmitted) {
    CallToAction = JoinSessionCta;
    // We suppress all CTAs except for the join session CTA when the client has
    // outstanding measures and the auto open measures drawer feature gate is
    // enabled to encourage measure completion.
  } else if (isAutoOpenMeasuresDrawerEnabled && hasOutstandingMeasures) {
    CallToAction = React.Fragment;
  } else if (isLoading) {
    // We also suppress the CTA while the page is loading, in case new data
    // coming in changes the decision about which CTA to show.
    CallToAction = React.Fragment;
  } else if (variant) {
    CallToAction = VariantComponents[variant];
  }

  const state = isAdmitted ? WaitingRoomState.ADMITTED : WaitingRoomState.AWAITING_ADMISSION;

  return (
    <PreSessionPrompt
      callToActionContent={<CallToAction openDrawer={openDrawer} state={state} variant={variant} />}
      hasOutstandingMeasures={hasOutstandingMeasures}
      isAutoOpenMeasuresDrawerEnabled={isAutoOpenMeasuresDrawerEnabled}
      state={state}
    />
  );
}
